import React, { useState, useMemo, useEffect } from 'react';
import { Card, Row, Col, ProgressBar, Table, Alert, Button, Spinner } from 'react-bootstrap';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { CME_USER_API, CME_DIEU_DUONG_API } from '../../../../utils/apiUrl';
import { Get, Post } from '../../../../utils/httpProvider';
import { toast } from 'react-toastify';
import { getUserSub } from '../../../../utils/helper';
import moment from 'moment';

const CMEDashboard = () => {
    const queryClient = useQueryClient();
    const currentYear = new Date().getFullYear();
    const [expandedDetail, setExpandedDetail] = useState(null); // { catName: string, status: number }
    const [currentUserId, setCurrentUserId] = useState(null);
    useEffect(() => {
        const fetchUserId = async () => {
            try {
                const sub = await getUserSub();
                setCurrentUserId(sub);
            } catch (err) {
                console.error("Failed to get current user ID", err);
            }
        };
        fetchUserId();
    }, []);

    // Query lấy danh sách lớp học để check-in/out nhanh cho điều dưỡng
    const { data: cmeEvents, refetch: refetchEvents } = useQuery(
        ['cmeDieuDuongEvents_Dashboard', currentUserId],
        async () => {
            try {
                const res = await Get(CME_DIEU_DUONG_API.GET_ALL);
                return res;
            } catch (err) {
                console.error("CME Dashboard API: GET_ALL error:", err);
                throw err;
            }
        },
        {
            enabled: !!currentUserId,
            staleTime: 60000,
            cacheTime: 90000
        }
    );

    // Mutation điểm danh
    const checkinMutation = useMutation(
        (eventId) => Post(`${CME_DIEU_DUONG_API.CHECKIN}/${eventId}`),
        {
            onSuccess: () => {
                toast.success("Điểm danh tham gia thành công!");
                refetchEvents();
                queryClient.invalidateQueries('cmeSummaryData');
            },
            onError: (err) => {
                toast.error(err?.message || "Lỗi khi điểm danh.");
            }
        }
    );

    const checkoutMutation = useMutation(
        (eventId) => Post(`${CME_DIEU_DUONG_API.CHECKOUT}/${eventId}`),
        {
            onSuccess: () => {
                toast.success("Điểm danh kết thúc thành công!");
                refetchEvents();
                queryClient.invalidateQueries('cmeSummaryData');
            },
            onError: (err) => {
                toast.error(err?.message || "Lỗi khi điểm danh.");
            }
        }
    );

    const handleCheckin = (eventId) => {
        checkinMutation.mutate(eventId);
    };

    const handleCheckout = (eventId) => {
        checkoutMutation.mutate(eventId);
    };

    // 1. Lấy dữ liệu tóm tắt (dùng cho các box thống kê)
    const { isLoading: loadingSummary, isError: errorSummary, data: summaryData } = useQuery(
        ['cmeSummaryData'],
        () => Get(CME_USER_API.GET_MY_SUMMARY),
        { keepPreviousData: true }
    );

    // 2. Lấy danh sách hồ sơ chi tiết (để tìm các hồ sơ cần gia hạn)
    const { isLoading: loadingHistory, data: historyQueryResult } = useQuery(
        ['cmeUserHistory_Dashboard'],
        () => Post(CME_USER_API.GET_MY_HO_SO, null, { PageIndex: 1, PageSize: 1000 }).then(res => res.json())
    );

    // Mutation xử lý gia hạn
    const cloneMutation = useMutation(
        (id) => Post(`${CME_USER_API.POST_USER_CLONE}/${id}`),
        {
            onSuccess: () => {
                toast.success("Đã gia hạn hồ sơ thành công vào danh sách của bạn!");
                queryClient.invalidateQueries('cmeSummaryData');
                queryClient.invalidateQueries('cmeUserHistory_Dashboard');
                queryClient.invalidateQueries('cmeUserHistory');
            },
            onError: (err) => {
                toast.error("Lỗi khi gia hạn: " + (err.message || "Vui lòng thử lại sau."));
            }
        }
    );

    // Helper: Lấy giá trị thuộc tính không phân biệt hoa thường
    const gv = (obj, key) => {
        if (!obj) return undefined;
        return obj[key] !== undefined ? obj[key] : obj[key.charAt(0).toUpperCase() + key.slice(1)];
    };

    // Helper: Lấy năm từ chuỗi ngày tháng an toàn
    const getYearSafe = (dateVal) => {
        if (!dateVal) return 0;
        const d = new Date(dateVal);
        const year = d.getFullYear();
        if (!isNaN(year) && year > 1900) return year;
        if (typeof dateVal === 'string' && dateVal.includes('/')) {
            const parts = dateVal.split('/');
            const y = parseInt(parts[parts.length - 1]);
            if (!isNaN(y)) return y;
        }
        return 0;
    };

    const summaryItems = summaryData?.$values || summaryData?.data || summaryData || [];
    const historyItems = historyQueryResult?.Data?.$values || historyQueryResult?.Data || historyQueryResult?.data?.$values || historyQueryResult?.data || [];

    // Tối ưu hóa thống kê: Sử dụng dữ liệu tổng hợp từ Backend (Ground Truth)
    const stats = useMemo(() => {
        const yearSummaryItems = summaryItems.filter(x => gv(x, 'nam') === currentYear);

        const totalHoursYear = yearSummaryItems.reduce((sum, item) => sum + (gv(item, 'tongSoGio') || 0), 0);
        const totalRecordsYear = yearSummaryItems.reduce((sum, item) => sum + (gv(item, 'soHoSo') || 0), 0);
        const totalPendingRecordsYear = yearSummaryItems.reduce((sum, item) => sum + (gv(item, 'soHoSoChoDuyet') || 0), 0);
        const totalRejectedRecordsYear = yearSummaryItems.reduce((sum, item) => sum + (gv(item, 'soHoSoTuChoi') || 0), 0);

        const startYearCycle = Math.max(2026, currentYear - 4);
        const endYearCycle = startYearCycle + 4;
        const cycleItems = summaryItems.filter(x => gv(x, 'nam') >= startYearCycle && gv(x, 'nam') <= currentYear);
        const totalHoursCycle = cycleItems.reduce((sum, item) => sum + (gv(item, 'tongSoGio') || 0), 0);
        const progressPercent = Math.min(100, Math.round((totalHoursYear / 24) * 100));

        // TỐI ƯU: Tìm hồ sơ cần gia hạn (Renewal) sử dụng Index để tra cứu nhanh (O(N))
        const currentYearRenewalGocIds = new Set(
            historyItems
                .filter(item => gv(item, 'namTichLuy') === currentYear && !gv(item, 'isDeleted') && gv(item, 'hoSoGocId'))
                .map(item => gv(item, 'hoSoGocId').toString().toLowerCase())
        );

        const ongoingActivities = historyItems.filter(h => {
            const status = gv(h, 'trangThai');
            const namTichLuy = gv(h, 'namTichLuy');
            const isAnnualRenewal = gv(h, 'isAnnualRenewal');
            const denNgayYear = getYearSafe(gv(h, 'denNgay'));
            const hId = gv(h, 'id')?.toString().toLowerCase();

            // Chỉ xét hồ sơ GỐC đã DUYỆT từ năm cũ, có cấu hình gia hạn và còn thời hạn
            if (status !== 2 || namTichLuy >= currentYear || !isAnnualRenewal || denNgayYear < currentYear) return false;

            // Kiểm tra nhanh xem đã có bản gia hạn cho năm nay chưa
            return !currentYearRenewalGocIds.has(hId);
        });

        return {
            totalHoursYear,
            totalRecordsYear,
            totalPendingRecordsYear,
            totalRejectedRecordsYear,
            yearSummaryItems,
            totalHoursCycle,
            progressPercent,
            startYearCycle,
            endYearCycle,
            ongoingActivities
        };
    }, [summaryItems, historyItems, currentYear]);

    const {
        totalHoursYear,
        totalRecordsYear,
        totalPendingRecordsYear,
        totalRejectedRecordsYear,
        yearSummaryItems,
        totalHoursCycle,
        progressPercent,
        startYearCycle,
        endYearCycle,
        ongoingActivities
    } = stats;

    const cycleYears = useMemo(() => {
        const years = [];
        // Tạo danh sách 5 năm tính từ startYearCycle
        for (let i = 0; i < 5; i++) {
            years.push(startYearCycle + i);
        }
        // Sắp xếp giảm dần để năm mới nhất ở trên
        return years.sort((a, b) => b - a);
    }, [startYearCycle]);

    const activeAttendanceEvents = useMemo(() => {
        if (!cmeEvents) return [];
        const list = cmeEvents?.$values || cmeEvents || [];
        const filtered = list.filter(item => {
            const listDiemDanh = gv(item, 'cmeDiemDanhDieuDuongs') || gv(item, 'cmeDiemDanhDieuDuongs')?.$values || item.CMEDiemDanhDieuDuongs?.$values || item.cmeDiemDanhDieuDuongs?.$values || item.CMEDiemDanhDieuDuongs || item.cmeDiemDanhDieuDuongs || [];
            const myRecord = listDiemDanh.find(d => d.userId === currentUserId || d.UserId === currentUserId);
            const hasCheckedIn = !!myRecord?.thoiGianDen || !!myRecord?.ThoiGianDen;
            const hasCheckedOut = !!myRecord?.thoiGianVe || !!myRecord?.ThoiGianVe;
            const trangThai = gv(item, 'trangThai');

            // Nếu sự kiện đã kết thúc hoàn toàn thì ẩn (giữ lại các sự kiện chưa kết thúc kể cả khi đã điểm danh xong cả Đến và Về để học viên theo dõi trạng thái duyệt)
            if (Number(trangThai) === 2) return false;

            const choPhepDiemDanhBatDau = gv(item, 'choPhepDiemDanhBatDau');
            const choPhepDiemDanhKetThuc = gv(item, 'choPhepDiemDanhKetThuc');
            const denNgay = gv(item, 'denNgay');

            // Hỗ trợ hiển thị các sự kiện chưa bắt đầu, đang diễn ra và chưa kết thúc (ngày kết thúc >= hôm nay)
            const isSapHoacDangDienRa = (Number(trangThai) === 0 || Number(trangThai) === 1) && moment(denNgay).isSameOrAfter(moment(), 'day');

            const isMatched = choPhepDiemDanhBatDau || choPhepDiemDanhKetThuc || isSapHoacDangDienRa || (hasCheckedIn && !hasCheckedOut && moment(denNgay).isSameOrAfter(moment(), 'day'));
            return isMatched;
        });
        return filtered;
    }, [cmeEvents, currentUserId]);

    return (
        <div className="container-fluid py-3">
            <h4 className="mb-4">Bảng điều khiển CME - Năm {currentYear}</h4>

            <Row className="mb-4">
                <Col md={3} className="mb-3 mb-md-0">
                    <Card className="text-white cme-stat-card cme-card-hours shadow border-0">
                        <Card.Body className="p-4">
                            <div className="d-flex justify-content-between align-items-center">
                                <div>
                                    <h6 className="cme-card-title">Tổng giờ đạt</h6>
                                    <h2 className="cme-card-value">{totalHoursYear} <span className="cme-card-unit">giờ</span></h2>
                                </div>
                                <div className="cme-icon-container">
                                    <i className="fas fa-clock fa-lg"></i>
                                </div>
                            </div>
                        </Card.Body>
                    </Card>
                </Col>
                <Col md={3} className="mb-3 mb-md-0">
                    <Card className="text-white cme-stat-card cme-card-approved shadow border-0">
                        <Card.Body className="p-4">
                            <div className="d-flex justify-content-between align-items-center">
                                <div>
                                    <h6 className="cme-card-title">Đã duyệt</h6>
                                    <h2 className="cme-card-value">{totalRecordsYear} <span className="cme-card-unit">hồ sơ</span></h2>
                                </div>
                                <div className="cme-icon-container">
                                    <i className="fas fa-check-circle fa-lg"></i>
                                </div>
                            </div>
                        </Card.Body>
                    </Card>
                </Col>
                <Col md={3} className="mb-3 mb-md-0">
                    <Card className="text-white cme-stat-card cme-card-pending shadow border-0">
                        <Card.Body className="p-4">
                            <div className="d-flex justify-content-between align-items-center">
                                <div>
                                    <h6 className="cme-card-title">Đang chờ</h6>
                                    <h2 className="cme-card-value">{totalPendingRecordsYear} <span className="cme-card-unit">hồ sơ</span></h2>
                                </div>
                                <div className="cme-icon-container">
                                    <i className="fas fa-hourglass-half fa-lg"></i>
                                </div>
                            </div>
                        </Card.Body>
                    </Card>
                </Col>
                <Col md={3} className="mb-3 mb-md-0">
                    <Card className="text-white cme-stat-card cme-card-rejected shadow border-0">
                        <Card.Body className="p-4">
                            <div className="d-flex justify-content-between align-items-center">
                                <div>
                                    <h6 className="cme-card-title">Từ chối</h6>
                                    <h2 className="cme-card-value">{totalRejectedRecordsYear} <span className="cme-card-unit">hồ sơ</span></h2>
                                </div>
                                <div className="cme-icon-container">
                                    <i className="fas fa-times-circle fa-lg"></i>
                                </div>
                            </div>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>

            {activeAttendanceEvents.length > 0 && (
                <Card className="mb-4 shadow border-0" style={{ borderRadius: '16px', background: 'linear-gradient(135deg, #fdfbf7 0%, #f7f3e9 100%)', borderLeft: '5px solid #28a745' }}>
                    <Card.Body className="p-4">
                        <h5 className="font-weight-bold text-success mb-3">
                            <i className="fas fa-clipboard-check mr-2"></i> SỰ KIỆN CME & ĐIỂM DANH
                        </h5>
                        <div className="row">
                            {activeAttendanceEvents.map((item) => {
                                const listDiemDanh = gv(item, 'cmeDiemDanhDieuDuongs') || gv(item, 'cmeDiemDanhDieuDuongs')?.$values || item.CMEDiemDanhDieuDuongs?.$values || item.cmeDiemDanhDieuDuongs?.$values || item.CMEDiemDanhDieuDuongs || item.cmeDiemDanhDieuDuongs || [];
                                const myRecord = listDiemDanh.find(d => d.userId === currentUserId || d.UserId === currentUserId);

                                const hasCheckedIn = !!myRecord?.thoiGianDen || !!myRecord?.ThoiGianDen;
                                const hasCheckedOut = !!myRecord?.thoiGianVe || !!myRecord?.ThoiGianVe;
                                const thoiGianDen = myRecord?.thoiGianDen || myRecord?.ThoiGianDen;
                                const thoiGianVe = myRecord?.thoiGianVe || myRecord?.ThoiGianVe;

                                const eventId = gv(item, 'id');
                                const tenKhoaDaoTao = gv(item, 'tenKhoaDaoTao');
                                const tuNgay = gv(item, 'tuNgay');
                                const denNgay = gv(item, 'denNgay');
                                const diaDiem = gv(item, 'diaDiem');
                                const soGioTinChi = gv(item, 'soGioTinChi');
                                const chuTri = gv(item, 'chuTri') || gv(item, 'ChuTri');
                                const baoCaoVien = gv(item, 'baoCaoVien') || gv(item, 'BaoCaoVien');
                                const tenBaoCao = gv(item, 'tenBaoCao') || gv(item, 'TenBaoCao');
                                const choPhepDiemDanhBatDau = gv(item, 'choPhepDiemDanhBatDau');
                                const choPhepDiemDanhKetThuc = gv(item, 'choPhepDiemDanhKetThuc');
                                const trangThai = gv(item, 'trangThai');

                                const authors = (baoCaoVien || '').split('; ').filter(Boolean);
                                const titles = (tenBaoCao || '').split('; ').filter(Boolean);
                                const speakerReports = [];
                                const maxLength = Math.max(authors.length, titles.length);
                                for (let i = 0; i < maxLength; i++) {
                                    const author = authors[i] || '';
                                    const title = titles[i] || '';
                                    if (author && title) {
                                        speakerReports.push(`${author} (${title})`);
                                    } else {
                                        speakerReports.push(author || title);
                                    }
                                }

                                return (
                                    <div key={eventId} className="col-12 p-3 mb-2 rounded bg-white shadow-sm d-flex flex-column flex-md-row justify-content-between align-items-md-center border">
                                        <div className="mb-3 mb-md-0" style={{ flex: 1, paddingRight: '15px' }}>
                                            <div className="font-weight-bold text-dark" style={{ fontSize: '1.05rem' }}>{tenKhoaDaoTao}</div>
                                            <div className="small text-muted mt-1">
                                                <i className="far fa-calendar-alt mr-1"></i>Từ: {moment(tuNgay).format('DD/MM/YYYY')} - Đến: {moment(denNgay).format('DD/MM/YYYY')}
                                                {diaDiem && <span className="ml-3"><i className="fas fa-map-marker-alt mr-1"></i>{diaDiem}</span>}
                                                <span className="ml-3"><i className="fas fa-award mr-1"></i>{soGioTinChi}h CME</span>
                                                {chuTri && <span className="ml-3"><i className="fas fa-user-tie mr-1"></i>Chủ trì: {chuTri}</span>}
                                                {speakerReports.length > 0 && (
                                                    <span className="ml-3">
                                                        <i className="fas fa-user-md mr-1"></i>
                                                        Báo cáo viên: {speakerReports.join(', ')}
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                        <div className="d-flex flex-column align-items-md-end justify-content-center" style={{ minWidth: '240px', gap: '8px' }}>
                                            <div className="d-flex flex-wrap" style={{ gap: '8px' }}>
                                                {Number(trangThai) === 0 && !hasCheckedIn && (
                                                    <span className="badge badge-secondary p-2 font-weight-bold d-inline-flex align-items-center" style={{ fontSize: '0.75rem' }}>
                                                        <i className="fas fa-hourglass-start mr-1"></i>Chưa bắt đầu
                                                    </span>
                                                )}
                                                {Number(trangThai) === 1 && !choPhepDiemDanhBatDau && !choPhepDiemDanhKetThuc && (
                                                    <span className="badge badge-warning text-white p-2 font-weight-bold d-inline-flex align-items-center" style={{ fontSize: '0.75rem' }}>
                                                        <i className="fas fa-play mr-1"></i>Đang diễn ra
                                                    </span>
                                                )}
                                                {Number(trangThai) === 2 && (
                                                    <span className="badge badge-success p-2 font-weight-bold d-inline-flex align-items-center" style={{ fontSize: '0.75rem' }}>
                                                        <i className="fas fa-check-circle mr-1"></i>Đã kết thúc
                                                    </span>
                                                )}
                                                {(choPhepDiemDanhBatDau || hasCheckedIn) && (
                                                    <Button
                                                        variant="success"
                                                        size="sm"
                                                        className="font-weight-bold"
                                                        onClick={() => handleCheckin(eventId)}
                                                        disabled={hasCheckedIn || checkinMutation.isLoading}
                                                    >
                                                        {hasCheckedIn
                                                            ? `Đã đến lúc ${moment(thoiGianDen).format('HH:mm')}`
                                                            : "Điểm danh Đến"
                                                        }
                                                    </Button>
                                                )}
                                                {(choPhepDiemDanhKetThuc || hasCheckedOut) && (
                                                    <Button
                                                        variant="info"
                                                        size="sm"
                                                        className="font-weight-bold text-white"
                                                        onClick={() => handleCheckout(eventId)}
                                                        disabled={hasCheckedOut || !hasCheckedIn || checkoutMutation.isLoading}
                                                    >
                                                        {hasCheckedOut
                                                            ? `Đã về lúc ${moment(thoiGianVe).format('HH:mm')}`
                                                            : "Điểm danh Về"
                                                        }
                                                    </Button>
                                                )}
                                            </div>
                                            {myRecord && (
                                                <div className="mt-1">
                                                    {(myRecord.trangThaiDuyet === 0 || myRecord.TrangThaiDuyet === 0) && <span className="badge badge-warning text-white p-2" style={{ fontSize: '0.75rem' }}>Chờ duyệt (Chờ PĐD duyệt)</span>}
                                                    {(myRecord.trangThaiDuyet === 1 || myRecord.TrangThaiDuyet === 1) && <span className="badge badge-warning text-white p-2" style={{ fontSize: '0.75rem' }}>Chờ duyệt (Chờ HLĐT duyệt)</span>}
                                                    {(myRecord.trangThaiDuyet === 2 || myRecord.TrangThaiDuyet === 2) && (
                                                        <div className="d-flex flex-column align-items-md-end">
                                                            <span className="badge badge-danger p-2" style={{ fontSize: '0.75rem' }}>Bị từ chối</span>
                                                            {(myRecord.lyDoTuChoi || myRecord.LyDoTuChoi) && <small className="text-danger mt-1">Lý do: {myRecord.lyDoTuChoi || myRecord.LyDoTuChoi}</small>}
                                                        </div>
                                                    )}
                                                    {(myRecord.trangThaiDuyet === 3 || myRecord.TrangThaiDuyet === 3) && <span className="badge badge-success p-2" style={{ fontSize: '0.75rem' }}><i className="fas fa-check-circle mr-1"></i>Đã cộng điểm</span>}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </Card.Body>
                </Card>
            )}

            <Row>
                <Col lg={8}>
                    <Card className="shadow-sm border-0 mb-4 h-100">
                        <Card.Header className="bg-white py-3 border-bottom-0">
                            <h6 className="mb-0 font-weight-bold text-dark">Chi tiết tích lũy định mức năm {currentYear}</h6>
                        </Card.Header>
                        <Card.Body className="pt-0">
                            {loadingSummary ? (
                                <div className="text-center py-5"><p>Đang tải dữ liệu...</p></div>
                            ) : errorSummary ? (
                                <Alert variant="danger">Không thể tải dữ liệu thống kê.</Alert>
                            ) : yearSummaryItems.length > 0 ? (
                                yearSummaryItems.map((item, idx) => {
                                    const hApproved = gv(item, 'soHoSo') || 0;
                                    const hPending = gv(item, 'soHoSoChoDuyet') || 0;
                                    const hRejected = gv(item, 'soHoSoTuChoi') || 0;
                                    const totalHours = gv(item, 'tongSoGio') || 0;
                                    const totalHoursPending = gv(item, 'tongSoGioChoDuyet') || 0;
                                    const target = 12; // Định mức danh mục 12h

                                    return (
                                        <div className="mb-4" key={idx}>
                                            <div className="d-flex justify-content-between align-items-center mb-1">
                                                <div className="font-weight-bold text-dark pr-3" style={{ fontSize: '0.9rem', lineHeight: '1.2' }}>
                                                    {item.tenDanhMuc}
                                                </div>
                                                <div className="text-info font-weight-bold" style={{ whiteSpace: 'nowrap' }}>{totalHours} giờ</div>
                                            </div>

                                            <div className="d-flex align-items-center small text-muted mb-2" style={{ gap: '15px' }}>
                                                <span
                                                    className={`cursor-pointer ${expandedDetail?.catName === item.tenDanhMuc && expandedDetail?.status === 2 ? 'font-weight-bold text-success' : ''}`}
                                                    onClick={() => setExpandedDetail(expandedDetail?.catName === item.tenDanhMuc && expandedDetail?.status === 2 ? null : { catName: item.tenDanhMuc, status: 2 })}
                                                >
                                                    <i className="fas fa-check text-success mr-1"></i> {hApproved} hồ sơ đã duyệt
                                                </span>
                                                <span
                                                    className={`cursor-pointer ${expandedDetail?.catName === item.tenDanhMuc && expandedDetail?.status === 1 ? 'font-weight-bold text-warning' : ''}`}
                                                    onClick={() => setExpandedDetail(expandedDetail?.catName === item.tenDanhMuc && expandedDetail?.status === 1 ? null : { catName: item.tenDanhMuc, status: 1 })}
                                                >
                                                    <i className="far fa-clock text-warning mr-1"></i> {hPending} đang chờ
                                                </span>
                                                <span
                                                    className={`cursor-pointer ${expandedDetail?.catName === item.tenDanhMuc && expandedDetail?.status === 3 ? 'font-weight-bold text-danger' : ''}`}
                                                    onClick={() => setExpandedDetail(expandedDetail?.catName === item.tenDanhMuc && expandedDetail?.status === 3 ? null : { catName: item.tenDanhMuc, status: 3 })}
                                                >
                                                    <i className="fas fa-times text-danger mr-1"></i> {hRejected} từ chối
                                                </span>
                                            </div>

                                            <ProgressBar style={{ height: '6px' }} className="shadow-none bg-light">
                                                <ProgressBar
                                                    variant="success"
                                                    now={Math.min(100, (totalHours / target) * 100)}
                                                    key={1}
                                                />
                                                <ProgressBar
                                                    variant="warning"
                                                    now={Math.min(100, (totalHoursPending / target) * 100)}
                                                    key={2}
                                                />
                                            </ProgressBar>

                                            {/* Drill-down Table */}
                                            {expandedDetail?.catName === item.tenDanhMuc && (
                                                <div className="mt-2 animate-fade-in bg-light p-2 rounded border">
                                                    <h6 className="small font-weight-bold mb-2 text-secondary px-1">
                                                        Danh sách hồ sơ {expandedDetail.status === 2 ? "đã duyệt" : expandedDetail.status === 1 ? "đang chờ" : "bị từ chối"}
                                                    </h6>
                                                    <div className="table-responsive">
                                                        <Table size="sm" bordered hover className="mb-0 bg-white">
                                                            <thead className="thead-light">
                                                                <tr style={{ fontSize: '0.75rem' }}>
                                                                    <th>Nội dung</th>
                                                                    <th className="text-center" style={{ width: '60px' }}>Số giờ</th>
                                                                    <th className="text-center" style={{ width: '90px' }}>Thời gian</th>
                                                                    {expandedDetail.status === 3 && <th>Lý do</th>}
                                                                </tr>
                                                            </thead>
                                                            <tbody style={{ fontSize: '0.75rem' }}>
                                                                {historyItems
                                                                    .filter(h => gv(h, 'tenDanhMuc') === item.tenDanhMuc && gv(h, 'namTichLuy') === currentYear && gv(h, 'trangThai') === expandedDetail.status)
                                                                    .map((h, i) => (
                                                                        <tr key={i}>
                                                                            <td className="text-truncate" style={{ maxWidth: '250px' }}>{gv(h, 'noiDung')}</td>
                                                                            <td className="text-center font-weight-bold text-info">{gv(h, 'soGioTinChi')}h</td>
                                                                            <td className="text-center text-muted">
                                                                                {new Date(gv(h, 'tuNgay')).toLocaleDateString('vi-VN')}
                                                                            </td>
                                                                            {expandedDetail.status === 3 && <td className="text-danger small">{gv(h, 'lyDoTuChoi') || '-'}</td>}
                                                                        </tr>
                                                                    ))
                                                                }
                                                            </tbody>
                                                        </Table>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    );
                                })
                            ) : (
                                <div className="text-center py-5">
                                    <i className="fas fa-folder-open fa-3x text-light mb-3"></i>
                                    <p className="text-muted">Chưa có hoạt động nào trong năm {currentYear}</p>
                                </div>
                            )}
                        </Card.Body>
                    </Card>
                </Col>

                <Col lg={4}>
                    <Card className="shadow-sm border-0 mb-4 bg-white">
                        <Card.Body className="text-center p-4">
                            <h6 className="font-weight-bold mb-3 small text-uppercase">Mục tiêu đào tạo năm {currentYear}</h6>
                            <div className="position-relative mb-4">
                                <h1 className="display-4 font-weight-bold text-primary mb-0">{totalHoursYear} <small className="h5 text-muted">/ 24</small></h1>
                                <p className="text-muted small mb-0">Giờ tín chỉ ĐÃ DUYỆT năm {currentYear}</p>
                            </div>
                            <ProgressBar
                                variant={progressPercent >= 100 ? "success" : "primary"}
                                now={progressPercent}
                                className="mb-2"
                                style={{ height: '10px', borderRadius: '5px' }}
                            />
                            <div className="d-flex justify-content-between small text-muted font-weight-bold">
                                <span>{progressPercent}% hoàn thành</span>
                                <span className="text-dark">Mục tiêu 24 giờ</span>
                            </div>
                        </Card.Body>
                    </Card>

                    {ongoingActivities.length > 0 && (
                        <Card className="shadow-sm border-0 mb-4 border-left-warning" style={{ borderLeft: '4px solid #ffc107' }}>
                            <Card.Header className="bg-white py-3 border-bottom-0 d-flex justify-content-between align-items-center">
                                <h6 className="mb-0 font-weight-bold text-dark small text-uppercase"><i className="fas fa-sync-alt mr-2 text-warning"></i>Cần gia hạn định kỳ</h6>
                                <span className="badge badge-warning">{ongoingActivities.length}</span>
                            </Card.Header>
                            <Card.Body className="pt-0">
                                {ongoingActivities.map((h, i) => (
                                    <div key={i} className="p-2 mb-2 border rounded bg-light d-flex justify-content-between align-items-center">
                                        <div style={{ flex: 1, paddingRight: '10px', overflow: 'hidden' }}>
                                            <div className="font-weight-bold text-truncate small" title={gv(h, 'noiDung')}>{gv(h, 'noiDung')}</div>
                                            <div className="small text-muted" style={{ fontSize: '0.7rem' }}>Năm gốc: {gv(h, 'namTichLuy')}</div>
                                        </div>
                                        <Button
                                            variant="warning"
                                            size="sm"
                                            className="font-weight-bold py-1 px-2"
                                            style={{ fontSize: '0.65rem' }}
                                            onClick={() => cloneMutation.mutate(gv(h, 'id'))}
                                            disabled={cloneMutation.isLoading}
                                        >
                                            {cloneMutation.isLoading ? <Spinner animation="border" size="sm" /> : "Gia hạn"}
                                        </Button>
                                    </div>
                                ))}
                            </Card.Body>
                        </Card>
                    )}

                    <Card className="shadow-sm border-0">
                        <Card.Body className="p-3">
                            <h6 className="font-weight-bold mb-3 small text-uppercase text-muted"><i className="fas fa-history mr-2"></i>TÓM TẮT CHU KỲ ({startYearCycle} - {endYearCycle})</h6>
                            <div className="d-flex justify-content-between align-items-end mb-1">
                                <div className="h4 mb-0 font-weight-bold text-dark">{totalHoursCycle} <small className="h6 text-muted">/ 120 giờ</small></div>
                                <div className="small font-weight-bold text-info">{Math.min(100, Math.round((totalHoursCycle / 120) * 100))}%</div>
                            </div>
                            <ProgressBar variant="info" now={Math.min(100, (totalHoursCycle / 120) * 100)} className="mb-4" style={{ height: '6px' }} />

                            {cycleYears.map(year => {
                                const yearTotal = summaryItems.filter(x => gv(x, 'nam') === year).reduce((sum, item) => sum + (gv(item, 'tongSoGio') || 0), 0);
                                const isCurrent = year === currentYear;
                                return (
                                    <div key={year} className={`d-flex justify-content-between align-items-center mb-2 pb-2 border-bottom last-border-0 ${isCurrent ? 'text-primary' : ''}`}>
                                        <div className="font-weight-bold small">
                                            {isCurrent && <i className="fas fa-caret-right mr-1"></i>}
                                            Năm {year} {isCurrent && "(Hiện tại)"}
                                        </div>
                                        <div className={`badge ${isCurrent ? 'badge-primary' : 'badge-light'} p-2`} style={{ fontSize: '0.85rem' }}>{yearTotal} giờ</div>
                                    </div>
                                );
                            })}

                            <Alert variant="info" className="small mt-3 mb-0 border-0 p-2" style={{ fontSize: '0.75rem' }}>
                                <i className="fas fa-info-circle mr-1"></i> Quy định: Hoàn thành đủ 120 giờ trong chu kỳ xét duyệt 5 năm.
                            </Alert>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>

            <style>{`
                .transition-all { transition: all 0.2s ease-in-out; }
                .bg-white-20 { background-color: rgba(255,255,255,0.2); }
                .opa-8 { opacity: 0.8; }
                .animate-fade-in { animation: fadeIn 0.3s ease-out; }
                @keyframes fadeIn { from { opacity: 0; transform: translateY(5px); } to { opacity: 1; transform: translateY(0); } }
                .last-border-0:last-child { border-bottom: 0 !important; }
                .cursor-pointer { cursor: pointer; }
                .italic { font-style: italic; }

                /* --- CMEDashboard Premium Card Styles --- */
                .cme-stat-card {
                    border: none !important;
                    border-radius: 16px !important;
                    overflow: hidden;
                    position: relative;
                    box-shadow: 0 6px 20px rgba(0, 0, 0, 0.06) !important;
                }

                /* Hiệu ứng ánh gương mờ bề mặt */
                .cme-stat-card::before {
                    content: '';
                    position: absolute;
                    top: 0;
                    left: 0;
                    width: 100%;
                    height: 100%;
                    background: linear-gradient(135deg, rgba(255, 255, 255, 0.15) 0%, rgba(255, 255, 255, 0) 60%);
                    pointer-events: none;
                    z-index: 1;
                }

                /* Định nghĩa các dải màu Gradient cao cấp */
                .cme-card-hours {
                    background: linear-gradient(135deg, #00c6ff 0%, #0072ff 100%) !important;
                }
                .cme-card-approved {
                    background: linear-gradient(135deg, #11998e 0%, #38ef7d 100%) !important;
                }
                .cme-card-pending {
                    background: linear-gradient(135deg, #f12711 0%, #f5af19 100%) !important;
                }
                .cme-card-rejected {
                    background: linear-gradient(135deg, #e52d27 0%, #b31217 100%) !important;
                }

                /* Glassmorphism Container cho Icon */
                .cme-icon-container {
                    background: rgba(255, 255, 255, 0.18);
                    backdrop-filter: blur(8px);
                    -webkit-backdrop-filter: blur(8px);
                    border: 1px solid rgba(255, 255, 255, 0.25);
                    width: 48px;
                    height: 48px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    border-radius: 14px;
                    box-shadow: 0 4px 10px rgba(0, 0, 0, 0.05);
                }

                /* Icon bên trong container */
                .cme-icon-container i {
                    display: inline-block;
                }

                /* Định dạng typography và bóng chữ */
                .cme-card-title {
                    font-size: 0.78rem;
                    font-weight: 700;
                    letter-spacing: 1px;
                    text-transform: uppercase;
                    opacity: 0.9;
                    margin-bottom: 8px;
                    text-shadow: 0 1px 2px rgba(0, 0, 0, 0.15);
                }

                .cme-card-value {
                    font-size: 1.85rem;
                    font-weight: 800;
                    margin-bottom: 0;
                    line-height: 1.1;
                    letter-spacing: -0.5px;
                    text-shadow: 0 2px 4px rgba(0, 0, 0, 0.18);
                }

                .cme-card-unit {
                    font-size: 0.95rem;
                    font-weight: 500;
                    opacity: 0.85;
                    margin-left: 2px;
                }
            `}</style>
        </div>
    );
};

export default CMEDashboard;
